'use client';
import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

type DarkCircleInfo = {
    centerX: number;
    centerY: number;
    radius: number;
    containerTop: number;
    containerBottom: number;
};

type PageScrollContextValue = {
    darkCircleInfo: DarkCircleInfo | null;
    updateDarkCircle: (info: DarkCircleInfo) => void;
    calculateFadeOpacity: (elementRect: DOMRect) => number;
};

const PageScrollContext = createContext<PageScrollContextValue | null>(null);

export const usePageScroll = () => {
    const context = useContext(PageScrollContext);
    // Return null if not within provider - components can handle this gracefully
    return context;
};

type PageScrollProviderProps = {
    children: ReactNode;
    /** Distance to start fading as ratio of viewport height (default: 0.3 = 30vh) */
    fadeDistance?: number;
};

export const PageScrollProvider = ({ children, fadeDistance = 0.3 }: PageScrollProviderProps) => {
    const [darkCircleInfo, setDarkCircleInfo] = useState<DarkCircleInfo | null>(null);

    const updateDarkCircle = useCallback((info: DarkCircleInfo) => {
        setDarkCircleInfo(info);
    }, []);

    const calculateFadeOpacity = useCallback(
        (elementRect: DOMRect): number => {
            if (!darkCircleInfo) return 1;

            // Convert viewport ratio to pixels
            const fadeDistancePx = fadeDistance * window.innerHeight;

            const { centerY, radius, containerTop, containerBottom } = darkCircleInfo;

            // Element is inside the dark circle container, don't fade it
            if (elementRect.top >= containerTop && elementRect.bottom <= containerBottom) {
                return 1;
            }

            // Calculate circle edges
            const circleTopEdge = centerY - radius;
            const circleBottomEdge = centerY + radius;

            // Element is ABOVE the circle container - check if circle top is approaching
            if (elementRect.bottom < containerTop) {
                const distanceToCircleTop = circleTopEdge - elementRect.bottom;

                if (distanceToCircleTop > fadeDistancePx) {
                    return 1; // Far from circle, fully visible
                }
                if (distanceToCircleTop <= 0) {
                    return 0; // Circle has passed element
                }
                // Fade out as circle approaches from below
                const progress = distanceToCircleTop / fadeDistancePx;
                return Math.pow(progress, 0.5);
            }

            // Check if circle bottom is leaving
            if (elementRect.top > containerBottom) {
                const distanceFromCircleBottom = elementRect.top - circleBottomEdge;

                if (distanceFromCircleBottom > fadeDistancePx) {
                    return 1; // Far from circle, fully visible
                }
                if (distanceFromCircleBottom <= 0) {
                    return 0; // Circle still covers element
                }
                // Fade in as circle moves away upward
                const progress = distanceFromCircleBottom / fadeDistancePx;
                return progress; // Linear fade in
            }

            // Element overlaps with circle container
            return 1;
        },
        [darkCircleInfo, fadeDistance]
    );

    return (
        <PageScrollContext.Provider
            value={{
                darkCircleInfo,
                updateDarkCircle,
                calculateFadeOpacity,
            }}
        >
            {children}
        </PageScrollContext.Provider>
    );
};

export default PageScrollProvider;
